import { get, post, put, del } from '../utils/request';
import { UserRole, UserStatus } from '../constants/user';

// User interfaces
export interface User {
  id: number;
  username: string;
  email: string;
  real_name?: string;
  phone?: string;
  department?: string;
  avatar?: string;
  role: UserRole;
  status: UserStatus;
  created_at: string;
  updated_at: string;
}

export interface UserAssignee {
  id: number;
  real_name: string;
  department?: string;
}

export interface UserListRequest {
  skip?: number;
  limit?: number;
  role?: UserRole;
  status?: UserStatus;
}

export interface UserListResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  real_name?: string;
  phone?: string;
  department?: string;
  role?: UserRole;
}

export interface UpdateUserRequest {
  real_name?: string;
  phone?: string;
  department?: string;
  role?: UserRole;
  status?: UserStatus;
  role_override?: boolean;
  max_leads?: number;
}

// API methods

// Get current user
export const getCurrentUser = async (): Promise<User> => {
  return get<User>('/users/me');
};

// Update current user
export const updateCurrentUser = async (data: UpdateUserRequest): Promise<User> => {
  return put<User>('/users/me', data);
};

// Get all users (admin only)
export const getUsers = async (params?: UserListRequest): Promise<UserListResponse> => {
  return get<UserListResponse>('/users/', { params });
};

// Get user by ID (admin only)
export const getUser = async (userId: number): Promise<User> => {
  return get<User>(`/users/${userId}`);
};

// Create user (admin only)
export const createUser = async (data: CreateUserRequest): Promise<User> => {
  return post<User>('/users/', data);
};

// Update user (admin only)
export const updateUser = async (userId: number, data: UpdateUserRequest): Promise<User> => {
  return put<User>(`/users/${userId}`, data);
};

// Delete user (admin only)
export const deleteUser = async (userId: number): Promise<void> => {
  return del(`/users/${userId}`);
};

// Get assignable users (admin + staff, for assignment dropdowns)
export const getAssignableUsers = async (params?: { skip?: number; limit?: number }): Promise<UserAssignee[]> => {
  return get<UserAssignee[]>('/users/assignable', { params });
};

// Get staff users (for assignment dropdowns)
export const getStaff = async (params?: { skip?: number; limit?: number }): Promise<User[]> => {
  return get<User[]>('/users/staff', { params });
};

// Get lawyers for case assignment (STAFF + LAWYER roles)
export const getLawyersForCase = async (params?: { skip?: number; limit?: number }): Promise<User[]> => {
  return get<User[]>('/users/lawyers-for-case', { params });
};